/**
 * SESIÓN OFFLINE ACTIVA (FASE 5.6)
 *
 * Lo que las pantallas de `/offline*` necesitan para operar, una vez
 * `PuertaOfflineSegura` (`paginas/offline-perfil.tsx`) ya confirmó que hay
 * un perfil local desbloqueado: el `perfilId` con el que dirigirse a
 * `offline/db.ts`, y el ROL efectivo con el que decidir qué UI mostrar.
 *
 * `roles` NUNCA es `usuario.roles` directamente — ver `offline-perfil.tsx`:
 * cuando hay sesión online válida, sí viene de ahí; cuando la pantalla se
 * abre sin red y sin haber podido validar sesión (recarga totalmente
 * offline, encargo §13/E2E escenario C), viene del `rol` cacheado en el
 * propio paquete local (`MetaOffline.rol`) — el ÚNICO dato de rol
 * disponible en ese momento, y suficiente porque el servidor vuelve a
 * autorizar cada mutación real al sincronizar (esto es sólo UX, igual
 * criterio que `tieneCapacidadOffline`).
 *
 * Sustituye a `usuario` dentro de estas pantallas — nunca se usa
 * `useSesion()` a la vez que este contexto para lo mismo, para no tener
 * dos fuentes del mismo dato.
 */
import { createContext, useContext, type ReactNode } from 'react';

export interface RolOffline {
  readonly rol: string;
  readonly idCentro: string | null;
}

export interface SesionOfflineActiva {
  readonly perfilId: string;
  readonly roles: readonly RolOffline[];
}

const ContextoPerfilOffline = createContext<SesionOfflineActiva | null>(null);

export function ProveedorPerfilOffline({
  valor,
  children,
}: {
  valor: SesionOfflineActiva;
  children: ReactNode;
}): ReactNode {
  return <ContextoPerfilOffline.Provider value={valor}>{children}</ContextoPerfilOffline.Provider>;
}

export function useSesionOfflineActiva(): SesionOfflineActiva {
  const v = useContext(ContextoPerfilOffline);
  if (v === null) throw new Error('useSesionOfflineActiva fuera de <ProveedorPerfilOffline>.');
  return v;
}
